import React from 'react';
import { useAppSelector } from 'hooks';

import { formatDateString, formatTimeString } from '~libs/dayjs';

import { AlertMessage } from '~components/form-fields/inputs';
import { InputProps } from '~components/form-fields/props';

import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectUsers } from '~care-workers/selectors/users';
import { selectClientPackageSchedules } from '~main/selectors/clients/clientPackageSchedules';
import { selectLookupClients } from '~main/selectors/clients/clients';

export const AddAppointmentSummary: React.FC<InputProps> = ({ value }) => {
  const clients = useAppSelector(selectLookupClients);
  const users = useAppSelector(selectUsers);
  const schedules = useAppSelector(selectClientPackageSchedules);
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);

  const { date, client_id, package_fixed_id, start_time, end_time, ends_next_day, service_type_id, user_id } = value;

  const client = clients.find(({ id }) => client_id === id);
  const user = users.find(({ id }) => user_id === id);
  const clientPackage = schedules.find((schedule) => Number(package_fixed_id) === schedule.package_id)?.package;
  const serviceType = serviceTypes.find(({ id }) => service_type_id === id);

  const endTimeDisplay = ends_next_day
    ? `${formatTimeString(end_time)} (Overnight)`
    : formatTimeString(start_time && end_time ? end_time : '');

  const renderSummaryRow = (label: string, value: React.ReactNode) => (
    <div className="row mb-2 p-1" key={label}>
      <div className="col-2">
        <strong>{label}:</strong>
      </div>
      <div className="col-8">{value}</div>
    </div>
  );

  return (
    <div>
      <AlertMessage
        id="confirm-alert"
        name="confirm-alert"
        value="Please note, this will save your appointment immediately. It will not be an unsaved change."
        type="alert-warning"
        color="warning"
      />
      {renderSummaryRow('Date', formatDateString(date) ?? 'N/A')}
      {renderSummaryRow('Client', client?.name ?? 'Not selected')}
      {renderSummaryRow('Package', clientPackage?.name ?? 'Not selected')}
      {renderSummaryRow(
        'Time',
        start_time && end_time ? `${formatTimeString(start_time)} - ${endTimeDisplay}` : 'Not set',
      )}
      {renderSummaryRow('Service Type', serviceType?.name ?? 'Not selected')}
      {renderSummaryRow('Care Worker', user?.full_name ?? 'Not assigned')}
    </div>
  );
};

export default AddAppointmentSummary;
